import { useSubscription } from '@apollo/client';
import { produce } from 'immer';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';

import {
  listenAppointmentLlmJobsSubscription,
  listenAppointmentSummarizationProgressSubscription,
} from './operations';
import { appointmentActivityFragment } from './useAppointmentActivity';
import { appointmentHeaderDetailsFragment } from './useAppointmentHeaderDetails';

const SUMMARIZATION_WATCH_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export const useAppointmentSummarizationWatch = ({
  appointmentId,
  tenantId,
  timeoutMs = SUMMARIZATION_WATCH_TIMEOUT_MS,
}: {
  appointmentId: string;
  tenantId: string;
  timeoutMs?: number;
}) => {
  const [streamingDateStart] = useState(new Date().toISOString());
  const [isWatchingForSummary, setIsWatchingForSummary] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stopWatchingForSummary = useCallback(() => {
    setIsWatchingForSummary(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const data = useCompleteFragment({
    fragment: appointmentHeaderDetailsFragment,
    key: {
      id: appointmentId,
    },
    strict: false,
  });

  const isSigned = Boolean(data?.doctor_interaction?.noteSignedAt);
  const isSummarized = Boolean(data?.humanOutputs?.length);

  const startWatchingForSummary = useCallback(() => {
    setIsWatchingForSummary(true);

    if (appointmentId) {
      cacheUtils.updateFragment(
        {
          fragment: appointmentActivityFragment,
          key: {
            id: appointmentId,
          },
        },
        (existing) => {
          if (!existing) {
            return existing;
          }
          return produce(existing, (draft) => {
            draft.appointment_llm_jobs.push({
              jobId: 'optimistic',
              __typename: 'AppointmentLlmJobs' as const,
              createdAt: new Date().toISOString(),
              jobStatus: 'PENDING_DATA_UPLOAD',
              llmOutputType: 'SOAP_NOTE',
              numAttempts: 0,
            });
          });
        },
      );
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsWatchingForSummary(false);
    }, timeoutMs);
  }, [appointmentId, timeoutMs]);

  useEffect(
    function stopWatchingIfSummarized() {
      if (isWatchingForSummary && isSummarized) {
        stopWatchingForSummary();
      }
    },
    [isWatchingForSummary, isSummarized, stopWatchingForSummary],
  );

  useEffect(function cleanupTimeoutOnUnmount() {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const shouldSkipSubscription =
    !appointmentId ||
    !tenantId ||
    isSigned ||
    !isWatchingForSummary ||
    isSummarized;

  useSubscription(listenAppointmentSummarizationProgressSubscription, {
    variables: {
      tenantId: tenantId,
      appointmentId: appointmentId,
    },
    skip: shouldSkipSubscription,
  });

  useSubscription(listenAppointmentLlmJobsSubscription, {
    variables: {
      tenantId: tenantId,
      appointmentId: appointmentId,
      initialValue: streamingDateStart,
    },
    skip: shouldSkipSubscription,
    onData: ({ data }) => {
      const jobUpdates = data.data?.appointmentLlmJobsStream;
      const soapNoteUpdate = jobUpdates?.find(
        (job) => job.llmOutputType === 'SOAP_NOTE',
      );
      if (soapNoteUpdate) {
        cacheUtils.updateFragment(
          {
            fragment: appointmentActivityFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }
            const final = produce(existing, (draft) => {
              const optimisticData = draft?.appointment_llm_jobs.findIndex(
                (job) => job.jobId === 'optimistic',
              );
              if (optimisticData !== -1) {
                draft.appointment_llm_jobs.splice(optimisticData, 1);
              }
              const existingJobIndex = draft.appointment_llm_jobs.findIndex(
                (job) => job.jobId === soapNoteUpdate.jobId,
              );
              if (existingJobIndex !== -1) {
                draft.appointment_llm_jobs[existingJobIndex] = soapNoteUpdate;
              } else {
                draft.appointment_llm_jobs.push(soapNoteUpdate);
              }
            });
            return final;
          },
        );
      }
    },
  });

  return {
    startWatchingForSummary,
    stopWatchingForSummary,
    isWatching: isWatchingForSummary,
  };
};
